import { AlertTriangle, Clock, CreditCard, FlaskConical, SlidersHorizontal } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMarketTimeZone } from '@/features/country-scope';
import { StatusBadge } from '@/shared/components/status-badge';
import { formatCurrency, formatDateTime } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';

import type {
  PricingCalculation,
  PricingProviderDecision,
  PricingStep,
} from '../types/pricing.types';
import { formatEngineValue, formatFraction } from './pricing-format';
import { WorkedFormula } from './worked-formula';

/*
 * Display only. Every number shown here was calculated by the checkout pricing
 * engine and arrives in the response; nothing below adds, multiplies or rounds
 * money — it only formats it.
 */

const SELECTION_REASONS: Record<string, string> = {
  LowestEligibleCost: 'Lowest cost among the providers checkout can use now.',
  TieLowerProviderId: 'Tied on the lowest cost; the rule picks the lower provider id.',
  OnlyEligible: 'The only provider checkout can use now.',
  Forced: 'Chosen by you for this simulation — not necessarily what checkout uses.',
};

const INELIGIBLE_REASONS: Record<string, string> = {
  MappingInactive: 'Mapping inactive',
  ProviderInactive: 'Provider inactive',
  ProductIdentifierMissing: 'Product id missing',
  ProviderCurrencyMissing: 'Provider has no currency',
  FxMissing: 'No exchange rate',
  FxStale: 'Exchange rate too old',
  BalanceInsufficient: 'Balance too low',
  ProviderDeprecated: 'Integration deprecated',
  OutOfStock: 'Out of stock',
  UnavailableAtCheckout: 'Could not sell it at checkout',
};

const HEALTH: Record<string, { label: string; variant: 'success' | 'warning' | 'info' }> = {
  Ok: { label: 'Healthy', variant: 'success' },
  AtNearFloor: { label: 'At / near minimum margin', variant: 'warning' },
  AtCeiling: { label: 'At maximum margin', variant: 'info' },
};

export function FinalPriceCard({ data }: { data: PricingCalculation }) {
  const zone = useMarketTimeZone();
  const health = data.result ? HEALTH[data.result.priceHealth] : undefined;
  const cost = data.steps.find((step) => step.code === 'PROVIDER_COST');
  const facts: [string, string][] = [
    ['Provider', data.provider?.selectedProviderName ?? '—'],
    ['Provider cost', formatEngineValue(cost?.result, cost?.resultCurrency)],
    ['Gross profit', formatEngineValue(data.result?.grossProfit, data.currency)],
    ['Realized margin', formatFraction(data.result?.realizedMargin)],
  ];
  return (
    <section
      aria-label="Final customer price"
      className="border-border bg-card overflow-hidden rounded-lg border"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 p-4">
        <div className="flex flex-col gap-1">
          <p className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
            Final customer price
          </p>
          <p
            className="text-4xl font-semibold tracking-tight tabular-nums"
            data-testid="final-price"
          >
            {formatCurrency(data.finalPrice, data.currency ?? 'USD')}
          </p>
          <p className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span className="inline-flex items-center gap-1">
              <CreditCard className="size-3.5" aria-hidden />
              {data.paymentChannelCode}
            </span>
            <span className="inline-flex items-center gap-1">
              <SlidersHorizontal className="size-3.5" aria-hidden />
              Pricing configuration v{data.configurationVersion ?? '—'}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3.5" aria-hidden />
              Calculated {formatDateTime(data.calculatedAtUtc, zone)}
            </span>
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {health && <StatusBadge variant={health.variant}>{health.label}</StatusBadge>}
          {data.result && data.result.appliedGuardrail !== 'None' && (
            <StatusBadge variant="warning">
              {data.result.appliedGuardrail === 'Floor'
                ? 'Minimum-margin floor applied'
                : 'Maximum-margin ceiling applied'}
            </StatusBadge>
          )}
        </div>
      </div>
      <dl className="border-border bg-muted/30 grid grid-cols-2 gap-px border-t sm:grid-cols-4">
        {facts.map(([label, text]) => (
          <div key={label} className="flex min-w-0 flex-col gap-0.5 px-4 py-2.5">
            <dt className="text-muted-foreground text-[11px]">{label}</dt>
            <dd className="truncate text-sm font-medium tabular-nums" title={text}>
              {text}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function CalculationSummary({ data }: { data: PricingCalculation }) {
  const zone = useMarketTimeZone();
  const steps = Object.fromEntries(data.steps.map((step) => [step.code, step]));
  const cost = steps.PROVIDER_COST;
  const fx = steps.CURRENCY_CONVERSION?.values.find((v) => v.kind === 'Rate');
  const rows: [string, string][] = [
    ['Provider', data.provider?.selectedProviderName ?? '—'],
    ['Provider cost', formatEngineValue(cost?.result, cost?.resultCurrency)],
    [
      'FX rate',
      fx ? `${formatEngineValue(fx.value)} (${formatDateTime(fx.at ?? null, zone)})` : '—',
    ],
    ['Payment channel', data.paymentChannelCode ?? '—'],
    ['Final price', formatCurrency(data.finalPrice, data.currency ?? 'USD')],
    ['Net revenue', formatEngineValue(data.result?.netRevenue, data.currency)],
    ['Gross profit', formatEngineValue(data.result?.grossProfit, data.currency)],
    ['Realized margin', formatFraction(data.result?.realizedMargin)],
  ];
  return (
    <section
      aria-label="Summary"
      className="border-border bg-card rounded-lg border lg:sticky lg:top-4"
    >
      <h2 className="border-border border-b px-3 py-2 text-sm font-semibold">Summary</h2>
      <dl className="divide-border divide-y text-xs">
        {rows.map(([label, text]) => (
          <div key={label} className="flex items-baseline justify-between gap-4 px-3 py-1.5">
            <dt className="text-muted-foreground shrink-0">{label}</dt>
            <dd
              className={cn(
                'text-end tabular-nums',
                label === 'Final price' && 'text-foreground font-semibold',
              )}
            >
              {text}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function CalculationSteps({ steps }: { steps: PricingStep[] }) {
  return (
    <section aria-label="Calculation breakdown">
      <h2 className="mb-2 text-sm font-semibold">Calculation breakdown</h2>
      <ol className="flex flex-col">
        {steps.map((step, index) => (
          <li key={step.code} className="relative flex gap-3 pb-2 last:pb-0">
            {index < steps.length - 1 && (
              <span aria-hidden className="bg-border absolute start-3 top-8 bottom-0 w-px" />
            )}
            <span
              aria-hidden
              className={cn(
                'bg-background relative mt-2.5 flex size-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold tabular-nums',
                step.highlight
                  ? 'border-status-warning-border bg-status-warning-bg text-status-warning'
                  : 'border-border text-muted-foreground',
              )}
            >
              {index + 1}
            </span>
            <div
              className={cn(
                'border-border bg-card min-w-0 flex-1 rounded-md border p-3 text-xs',
                step.highlight && 'border-status-warning-border bg-status-warning-bg',
              )}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-medium">
                  <span className="sr-only">Step {index + 1} – </span>
                  {step.label}
                </p>
                <p className="text-sm font-semibold tabular-nums">
                  {formatEngineValue(step.result, step.resultCurrency)}
                </p>
              </div>
              <p className="text-muted-foreground mt-0.5">{step.formula}</p>
              <WorkedFormula lines={step.working} />
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** The whole calculation in one formula, then what the business keeps from the price. */
export function FullFormula({
  data,
}: {
  data: Pick<PricingCalculation, 'fullFormula' | 'resultFormula'>;
}) {
  if (data.fullFormula.length === 0) return null;
  return (
    <section
      aria-label="Full formula"
      className="border-border bg-card rounded-lg border p-3 text-xs"
    >
      <h2 className="text-sm font-semibold">Full formula</h2>
      <p className="text-muted-foreground mt-0.5">
        Every step above in one formula, with the values the pricing engine used.
      </p>
      <WorkedFormula lines={data.fullFormula} label="Final price formula" />
      <WorkedFormula lines={data.resultFormula} label="Revenue and margin formula" />
    </section>
  );
}

export function ProviderDecision({ decision }: { decision: PricingProviderDecision }) {
  return (
    <section
      aria-label="Provider decision"
      className="border-border bg-card flex flex-col gap-2 rounded-lg border p-3"
    >
      <h2 className="text-sm font-semibold">Provider</h2>
      <p className="text-xs">
        {decision.selectedProviderName ? (
          <>
            <span className="font-medium">{decision.selectedProviderName}</span>
            {decision.selectionReason && (
              <span className="text-muted-foreground">
                {' '}
                — {SELECTION_REASONS[decision.selectionReason]}
              </span>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">No provider could be used.</span>
        )}
      </p>
      <div className="border-border overflow-x-auto rounded-md border">
        <Table className="text-xs">
          <TableHeader>
            <TableRow>
              <TableHead>Provider</TableHead>
              <TableHead className="text-end">Cost</TableHead>
              <TableHead className="text-end">Cost in USD</TableHead>
              <TableHead>Can checkout use it?</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {decision.candidates.map((candidate) => (
              <TableRow
                key={candidate.providerId}
                aria-selected={candidate.selected}
                className={cn(candidate.selected && 'bg-status-success-bg')}
              >
                <TableCell className="font-medium">
                  {candidate.providerName ?? `Provider ${candidate.providerId}`}
                  {candidate.selected && (
                    <StatusBadge variant="success" className="ms-2">
                      Selected
                    </StatusBadge>
                  )}
                </TableCell>
                <TableCell className="text-end tabular-nums">
                  {formatEngineValue(candidate.cost, candidate.currency)}
                </TableCell>
                <TableCell className="text-end tabular-nums">
                  {formatEngineValue(candidate.costInUsd, 'USD')}
                </TableCell>
                <TableCell>
                  {candidate.eligible ? (
                    <StatusBadge variant="success">Yes</StatusBadge>
                  ) : (
                    <StatusBadge variant="danger">
                      {INELIGIBLE_REASONS[candidate.ineligibleReason ?? ''] ??
                        candidate.ineligibleReason}
                    </StatusBadge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

/** A named provider is a simulation: say so, next to what checkout would really do. */
export function SimulationBanner({ data }: { data: PricingCalculation }) {
  if (!data.providerForced) return null;
  const checkout = data.checkout;
  return (
    <div
      role="note"
      className="border-status-info-border bg-status-info-bg flex items-start gap-2 rounded-md border p-3 text-xs"
    >
      <FlaskConical className="mt-0.5 size-4 shrink-0" aria-hidden />
      <p>
        Simulation with a chosen provider — not what checkout does.{' '}
        {checkout?.finalPrice != null &&
        checkout.providerId === data.provider?.selectedProviderId ? (
          <>
            Checkout uses the same provider, <strong>{checkout.providerName}</strong>, at{' '}
            <strong>{formatCurrency(checkout.finalPrice, data.currency ?? 'USD')}</strong>.
          </>
        ) : checkout?.finalPrice != null ? (
          <>
            Checkout would use <strong>{checkout.providerName}</strong> at{' '}
            <strong>{formatCurrency(checkout.finalPrice, data.currency ?? 'USD')}</strong>.
          </>
        ) : (
          <>Checkout cannot price this item right now.</>
        )}
      </p>
    </div>
  );
}

/** No price: the engine's own reason, and never a number. */
export function CalculationUnavailable({ data }: { data: PricingCalculation }) {
  const title =
    data.outcome === 'Refused'
      ? 'Checkout refuses this request'
      : data.status === 'Blocked'
        ? 'Pricing is blocked'
        : 'No price can be calculated';
  return (
    <div
      role="alert"
      className="border-destructive/40 bg-destructive/5 flex items-start gap-2 rounded-md border p-3 text-sm"
    >
      <AlertTriangle className="text-destructive mt-0.5 size-4 shrink-0" aria-hidden />
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-muted-foreground text-xs">{data.failureMessage}</p>
        {data.failureReason && (
          <p className="text-muted-foreground mt-1 font-mono text-[11px]">{data.failureReason}</p>
        )}
      </div>
    </div>
  );
}
